import { fs } from '@/theme/tokens';
import { useState } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import LinearProgress from '@mui/material/LinearProgress';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { DialogClose } from '@/components/DialogClose';
import { useStagedPreview, type SyncSource } from '@/api/hrms-sync';
import { errorMessage } from '@/lib/api-client';

const SYSTEM_COLS = new Set([
  'id', 'source_record_id', 'first_seen_at', 'last_synced_at', 'last_run_id',
  'deleted_at_source', 'promoted_ref', 'promoted_at', 'promote_error',
]);

/** Read-only view of what a source's staging table holds. */
export function StagedDataDialog({ source, onClose }: { source: SyncSource | null; onClose: () => void }) {
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const { data, isLoading, error } = useStagedPreview(source?.source_key ?? null, page, pageSize);

  const dataCols = (data?.columns ?? []).filter((c) => !SYSTEM_COLS.has(c));
  const tailCols = (data?.columns ?? []).filter((c) =>
    ['source_record_id', 'last_synced_at', 'promoted_ref', 'promote_error'].includes(c));

  return (
    <Dialog open={!!source} onClose={onClose} maxWidth="xl" fullWidth>
      <DialogTitle sx={{ pr: 6 }}>
        {source?.title}
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
          table {data?.table ?? source?.local_table} · {data?.total?.toLocaleString() ?? '…'} live row(s)
        </Typography>
        <DialogClose onClose={onClose} />
      </DialogTitle>
      <DialogContent dividers sx={{ p: 0 }}>
        {isLoading && <LinearProgress />}
        {error && <Alert severity="error" sx={{ m: 2 }}>{errorMessage(error)}</Alert>}
        {data && data.total === 0 && (
          <Box sx={{ p: 3 }}>
            <Typography color="text.secondary">Nothing stored yet. Sync this source first.</Typography>
          </Box>
        )}
        {data && data.total > 0 && (
          <TableContainer sx={{ maxHeight: '65vh' }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  {[...dataCols, ...tailCols].map((c) => (
                    <TableCell key={c} sx={{ whiteSpace: 'nowrap', fontSize: fs.small }}>
                      <Tooltip title={data.column_sources[c] ? `uKnowva field: ${data.column_sources[c]}` : 'sync bookkeeping'}>
                        <span>{c}</span>
                      </Tooltip>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {data.rows.map((r, i) => (
                  <TableRow key={String(r.id ?? i)} hover>
                    {[...dataCols, ...tailCols].map((c) => (
                      <TableCell key={c} sx={{
                        maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap', fontSize: fs.small,
                        color: c === 'promote_error' ? 'error.main' : undefined }}>
                        <Tooltip title={r[c] == null ? '' : String(r[c])}>
                          <span>{r[c] == null ? '' : String(r[c])}</span>
                        </Tooltip>
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
        {data && data.total > 0 && (
          <TablePagination
            component="div" count={data.total} page={page} rowsPerPage={pageSize}
            rowsPerPageOptions={[25, 50, 100, 250]}
            onPageChange={(_e, p) => setPage(p)}
            onRowsPerPageChange={(e) => { setPageSize(parseInt(e.target.value, 10)); setPage(0); }}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}
